<svelte:options immutable />

<script lang="ts">
  import type { Image } from "bildebanken-model";
  import { isSearchHit, type SearchHit } from "services/searchHit";
  import { getFallbackImageUrl, getThumbnailUrl } from "state/thumbnails";
  import ImagePlaceholder from "./ImagePlaceholder.svelte";
  import { mapImageToFolderImage, mapSearchHitsToFolderImage } from "services/folders/map";
  import type { ImageSource } from "state/page";

  type ImageToPreview = Image | SearchHit;
  export let image: ImageToPreview | undefined;
  export let toggleFullScreen: (() => void) | undefined = undefined;
  export let mode: "table" | "tile" | "detail";
  export let itemSource: ImageSource | undefined = undefined;

  function getProxy(image?: ImageToPreview) {
    if (!image) return;
    if (isSearchHit(image)) return image.previewUrl || getFallbackImageUrl();
    return image.proxy ?? getFallbackImageUrl();
  }

  function getThumbnail(image?: ImageToPreview) {
    if (!image) return;
    if (isSearchHit(image)) return image.thumbnailUrl || getFallbackImageUrl();
    return getThumbnailUrl(image);
  }

  function hasProxy(image?: ImageToPreview) {
    if (!image) return;
    if (isSearchHit(image)) return !!image.thumbnailUrl;
    return !!image.proxy;
  }

  function hasThumbnail(image?: ImageToPreview) {
    if (!image) return;
    if (isSearchHit(image)) return !!image.thumbnailUrl;
    return !!getThumbnailUrl(image);
  }

  function getPreviewUrl(image?: ImageToPreview) {
    switch (mode) {
      case "table":
      case "tile":
        return getThumbnail(image);
      case "detail":
        return getProxy(image);
      default:
        break;
    }
  }

  function hasPreviewUrl(image?: ImageToPreview) {
    switch (mode) {
      case "table":
      case "tile":
        return hasThumbnail(image);
      case "detail":
        return hasProxy(image);
      default:
        break;
    }
  }

  function imageIsImageType(image: ImageToPreview | undefined): image is Image {
    return !(image && "formData" in image.metadata);
  }

  $: itemState =
    image && (("itemState" in image && image.itemState) || ("state" in image && image.state));
  $: error = itemState === "error";

  $: height = image && imageIsImageType(image) && image?.technicalMetadata?.technical_image_height;
  $: width = image && imageIsImageType(image) && image?.technicalMetadata?.technical_image_width;

  $: imageSizeStyle = height && width ? `--org_height: ${height}px; --org_width: ${width}px` : "";
  function startDraggingImage(event: DragEvent) {
    const draggableImage =
      itemSource && image && !isSearchHit(image)
        ? mapImageToFolderImage(image, itemSource)
        : isSearchHit(image)
        ? mapSearchHitsToFolderImage([image])
        : undefined;

    if (draggableImage) {
      event.dataTransfer?.setData("text/plain", JSON.stringify([draggableImage]));
    }
  }
  function setBackgroundPattern(image: ImageToPreview | undefined) {
    return (
      image &&
      imageIsImageType(image) &&
      image?.technicalMetadata?.image_exif_ColorType === "RGB with Alpha"
    );
  }
</script>

{#if (itemState === "complete" || hasPreviewUrl(image)) && !error}
  {#if toggleFullScreen}
    <button
      type="button"
      aria-label="Vis bilde i fullskjerm"
      draggable="true"
      on:click={toggleFullScreen}
      on:dragstart={startDraggingImage}
      class={setBackgroundPattern(image) ? "background-pattern" : ""}
      style={imageSizeStyle}
    >
      <img src={getPreviewUrl(image)} alt="" on:error={() => (error = true)} />
    </button>
  {:else}
    <img
      draggable="true"
      on:dragstart={startDraggingImage}
      class={setBackgroundPattern(image) ? "background-pattern" : ""}
      style={imageSizeStyle}
      src={getPreviewUrl(image)}
      alt=""
      on:error={() => (error = true)}
    />
  {/if}
{:else if itemState === "new"}
  <ImagePlaceholder state="new" {mode} />
{:else}
  <ImagePlaceholder state="error" {mode} />
{/if}

<style>
  button {
    border: none;
    background: none;
    cursor: zoom-in;
    padding: 0;
    line-height: 0;
    display: block;
  }
  .background-pattern {
    background-image: linear-gradient(45deg, #c9c4c4 25%, transparent 25%),
      linear-gradient(-45deg, #c9c4c4 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #c9c4c4 75%),
      linear-gradient(-45deg, transparent 75%, #c9c4c4 75%);
    background-size: 20px 20px;
    background-position:
      0 0,
      0 10px,
      10px -10px,
      -10px 0px;
  }
  img {
    --org_width: 100%;
    --org_height: 100%;
    align-self: center;
    overflow: hidden;
    object-fit: contain;
    flex-grow: 1;
    margin: 0 auto;
    display: block;
    max-width: min(var(--org_width), 100%);
    max-height: min(var(--org_height), 100%);
  }
</style>
